import React, { useState, useEffect } from 'react';
import { getAllGalleryImages, deleteGalleryImage, updateGalleryImage, GalleryImage } from '../../../../app/helper/galleryService';
import { showSuccessToast, showErrorToast } from '../../../../app/helper/functions';

const GalleryList: React.FC = () => {
    const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [deleting, setDeleting] = useState<string | null>(null);
    const [showConfirmation, setShowConfirmation] = useState<string | null>(null);
    const [editingImage, setEditingImage] = useState<GalleryImage | null>(null);
    const [editTitle, setEditTitle] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        loadGalleryImages();
    }, []);

    const loadGalleryImages = async () => {
        try {
            setLoading(true);
            setError(null);
            const images = await getAllGalleryImages();
            setGalleryImages(images);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred fetching gallery images');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteImage = async (imageId: string) => {
        if (!imageId) return;

        try {
            setDeleting(imageId);
            const result = await deleteGalleryImage(imageId);

            if (result.success) {
                // Update the state to remove the deleted image
                setGalleryImages(prev => prev.filter(img => img.id !== imageId));
                showSuccessToast('Image deleted successfully');
                // Hide confirmation dialog
                setShowConfirmation(null);
            } else {
                showErrorToast(result.error || 'Failed to delete image');
            }
        } catch (err) {
            console.error('Error deleting image:', err);
            showErrorToast(err instanceof Error ? err.message : 'An error occurred deleting the image');
        } finally {
            setDeleting(null);
        }
    };

    const handleEditClick = (image: GalleryImage) => {
        setEditingImage(image);
        setEditTitle(image.title || '');
    };

    const handleSaveTitle = async () => {
        if (!editingImage || !editingImage.id) return;

        try {
            setSaving(true);
            const success = await updateGalleryImage(editingImage.id, { title: editTitle.trim() });

            if (success) {
                // Update the state with the new title
                setGalleryImages(prev =>
                    prev.map(img =>
                        img.id === editingImage.id ? { ...img, title: editTitle.trim() } : img
                    )
                );
                showSuccessToast('Title updated successfully');
                setEditingImage(null);
                setEditTitle('');
            } else {
                showErrorToast('Failed to update title');
            }
        } catch (err) {
            console.error('Error updating title:', err);
            showErrorToast(err instanceof Error ? err.message : 'An error occurred updating the title');
        } finally {
            setSaving(false);
        }
    };

    const handleCancelEdit = () => {
        setEditingImage(null);
        setEditTitle('');
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                <strong className="font-bold">Error!</strong>
                <span className="block sm:inline"> {error}</span>
            </div>
        );
    }

    if (galleryImages.length === 0) {
        return (
            <div className="text-center py-8">
                <h3 className="text-lg font-medium text-gray-300">No gallery images found</h3>
                <p className="mt-2 text-gray-400">Upload your first image to get started.</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-white">Gallery Images ({galleryImages.length})</h2>
                <button
                    onClick={loadGalleryImages}
                    className="text-blue hover:text-yellow text-sm font-medium"
                >
                    Refresh
                </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {galleryImages.map((image) => (
                    <div key={image.id} className="bg-blackDarkTheme rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 relative group">
                        <div className="aspect-square overflow-hidden">
                            <img
                                src={image.imageUrl}
                                alt={image.title || 'Gallery image'}
                                className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                            />
                        </div>
                        <div className="p-3">
                            {image.title && (
                                <h3 className="text-sm font-semibold text-white mb-1 line-clamp-1">{image.title}</h3>
                            )}
                            {!image.title && (
                                <h3 className="text-sm font-semibold text-gray-500 mb-1 line-clamp-1 italic">No title</h3>
                            )}
                            <div className="flex justify-between items-center">
                                <div className="text-gray-400 text-xs">
                                    {typeof image.createdAt === 'object' && image.createdAt?.toDate
                                        ? image.createdAt.toDate().toLocaleDateString()
                                        : typeof image.createdAt === 'string'
                                            ? new Date(image.createdAt).toLocaleDateString()
                                            : 'Unknown date'}
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => handleEditClick(image)}
                                        className="text-yellow-500 hover:text-yellow-400 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                        aria-label="Edit title"
                                        disabled={!image.id}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                    </button>
                                    <button
                                        onClick={() => setShowConfirmation(image.id || '')}
                                        className="text-red-500 hover:text-red-400 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                        aria-label="Delete image"
                                        disabled={!image.id}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Confirmation Dialog */}
                        {showConfirmation === image.id && (
                            <div className="absolute inset-0 bg-black bg-opacity-80 flex items-center justify-center p-4 z-10">
                                <div className="bg-gray-700 p-4 rounded-lg max-w-xs w-full text-center">
                                    <p className="text-white mb-4">Are you sure you want to delete this image?</p>
                                    <p className="text-gray-400 text-sm mb-4">This will remove it from the gallery (Firestore).</p>
                                    <div className="flex justify-center space-x-3">
                                        <button
                                            onClick={() => setShowConfirmation(null)}
                                            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500"
                                            disabled={deleting === image.id}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={() => image.id && handleDeleteImage(image.id)}
                                            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 flex items-center"
                                            disabled={deleting === image.id}
                                        >
                                            {deleting === image.id ? (
                                                <>
                                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    Deleting
                                                </>
                                            ) : (
                                                'Delete'
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Edit Title Modal */}
            {editingImage && (
                <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
                    <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full">
                        <h3 className="text-xl font-semibold text-white mb-4">Edit Image Title</h3>
                        <div className="mb-4">
                            <label className="block text-gray-300 text-sm font-medium mb-2">
                                Title
                            </label>
                            <input
                                type="text"
                                value={editTitle}
                                onChange={(e) => setEditTitle(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-yellow-500 focus:border-yellow-500"
                                placeholder="Enter image title"
                                autoFocus
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        handleSaveTitle();
                                    } else if (e.key === 'Escape') {
                                        handleCancelEdit();
                                    }
                                }}
                            />
                        </div>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={handleCancelEdit}
                                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500 transition-colors"
                                disabled={saving}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSaveTitle}
                                className="px-4 py-2 bg-yellow text-black rounded hover:bg-yellow-600 transition-colors flex items-center"
                                disabled={saving}
                            >
                                {saving ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Saving...
                                    </>
                                ) : (
                                    'Save'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GalleryList;

